import React, { useState, useRef } from 'react';
import { useInlineEdit } from './InlineEditContext';
import AdminService from '../../services/adminService';

const InlineEditableImage = ({ value = '', sectionKey, fieldPath, className = '', imgClassName = '' }) => {
  const { editing, updateField, token } = useInlineEdit();
  const [isEditing, setIsEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value || '');
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const startEdit = () => {
    if (!editing) return;
    setLocalVal(value || '');
    setIsEditing(true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const stopEdit = () => setIsEditing(false);

  const commit = async () => {
    try {
      await updateField({ sectionKey, fieldPath, value: localVal });
    } catch (e) {
      alert(e?.message || 'Échec de la mise à jour');
    } finally {
      stopEdit();
    }
  };

  const onFileSelected = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const url = await AdminService.uploadImage(file, token);
      setLocalVal(url || '');
      await updateField({ sectionKey, fieldPath, value: url || '' });
      setIsEditing(false);
    } catch (e) {
      alert(e?.message || 'Upload échoué');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={className}>
      {editing && !isEditing && (
        <button
          type="button"
          onClick={startEdit}
          className="inline-flex items-center justify-center text-white bg-primary/80 hover:bg-primary rounded-full w-8 h-8"
          aria-label="Modifier l'image"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l5-5a2 2 0 012 0l5 5M2 7a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V7zm13-1a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </button>
      )}
      {editing && isEditing && (
        <div className="flex flex-col gap-3 bg-gray-800/60 p-3 rounded-md">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={localVal}
              onChange={(e) => setLocalVal(e.target.value)}
              className="flex-1 bg-gray-800 text-white border border-white/30 focus:border-white px-2 py-1 rounded-md"
              placeholder="URL"
            />
            <button
              type="button"
              onClick={commit}
              className="inline-flex items-center justify-center text-xs text-white bg-primary/90 hover:bg-primary rounded-full px-3 h-8"
            >
              Enregistrer
            </button>
            <button
              type="button"
              onClick={stopEdit}
              className="inline-flex items-center justify-center text-xs text-white bg-gray-600 hover:bg-gray-700 rounded-full px-3 h-8"
            >
              Annuler
            </button>
          </div>
          <div
            className={`relative border-2 border-dashed rounded-md p-4 text-center ${isDragging ? 'border-primary bg-gray-700' : 'border-gray-500'}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer?.files?.[0];
              if (file) onFileSelected(file);
            }}
          >
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l5-5a2 2 0 012 0l5 5M2 7a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V7zm13-1a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <div className="text-sm text-white/90">Glisser-déposer une image ici</div>
              <div className="text-xs text-gray-300">ou</div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onFileSelected(file);
                }}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                className={`px-3 py-2 rounded-md text-sm ${uploading ? 'bg-gray-600 text-white' : 'bg-primary text-white hover:bg-primary/90'}`}
              >
                {uploading ? 'Téléversement…' : 'Choisir un fichier'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineEditableImage;
